import type {Workload} from "../3-workload-analysis/model/Workload.ts";
import type {WorkloadInput} from "../3-workload-analysis/model/WorkloadInput.ts";
import type {ScoringContext} from "./model/ScoringContext.ts";
import {scoreJob} from "./job/JobScorer.ts";
import {scorerConcurrency} from "./workload/ConcurrencyUpweight.ts";
import type {ScoreResult} from "./model/ScoreResult.ts";
import type {WorkloadJobHolder} from "../3-workload-analysis/model/WorkloadJobHolder.ts";

export interface WorkloadScore {
    workload: Workload
    score: ScoreResult<Workload>
    jobScores: Record<string, ScoreResult<WorkloadJobHolder>>
}

export interface ScoringResult {
    workloadScores: WorkloadScore[]
    totalWorkloads: number
}

/**
 * Score all workloads by scoring their connected jobs
 * Returns scored workloads with job-level scores aggregated
 */
export function performScoring(workloads: Workload[], workloadInputs: WorkloadInput[]): ScoringResult {
    const workloadScores: WorkloadScore[] = []
    
    // Create a map of workloadInput by project for quick lookup
    const inputMap = new Map<string, WorkloadInput>()
    for (const input of workloadInputs) {
        const key = `${input.projectId.groupName}/${input.projectId.projectName}`
        inputMap.set(key, input)
    }
    
    for (const workload of workloads) {
        try {
            // Get the corresponding workload input
            const key = `${workload.versionId.groupName}/${workload.versionId.projectName}`
            const workloadInput = inputMap.get(key)
            
            if (!workloadInput) {
                console.warn(`Could not find workload input for ${key}`)
                continue
            }
            
            // Create a scoring context for this workload
            const ctx: ScoringContext = {
                jobs: {}
            }
            
            // Score all connected jobs in this workload
            for (const jobName of workload.connectedJobs) {
                const job = workloadInput.jobsByName[jobName]
                if (job) {
                    try {
                        scoreJob(ctx, job)
                    } catch (e) {
                        // Log error but continue scoring other jobs
                        console.warn(`Failed to score job ${jobName} in workload ${key}:`, e)
                    }
                }
            }
            
            // Aggregate job scores into a workload score
            const jobScores = Object.values(ctx.jobs)
            const totalJobScore = jobScores.reduce((sum, result) => sum + result.score, 0)
            const averageJobScore = jobScores.length > 0 ? totalJobScore / jobScores.length : 0
            
            // Apply workload-level scorers
            const concurrencyScore = scorerConcurrency(workload)
            const workloadLevelScore = concurrencyScore?.score ?? 0
            
            // Combine job-level and workload-level scores
            const finalScore = averageJobScore + workloadLevelScore
            
            // Collect positive descriptions from job scores (score > 0 means good for migration)
            const positiveDescriptions: string[] = []
            jobScores.forEach(jobScore => {
                if (jobScore.score > 0 && jobScore.description) {
                    positiveDescriptions.push(...jobScore.description)
                }
                // Also check children for positive descriptions
                jobScore.children?.forEach(child => {
                    if (child.score > 0 && child.description) {
                        positiveDescriptions.push(...child.description)
                    }
                })
            })
            
            // Add concurrency description if present
            if (concurrencyScore && concurrencyScore.description) {
                positiveDescriptions.push(...concurrencyScore.description)
            }
            
            const workloadScore: ScoreResult<Workload> = {
                scorerId: "workload-scorer",
                scoredObject: workload,
                score: finalScore,
                children: concurrencyScore ? [...jobScores, concurrencyScore] : jobScores,
                description: [
                    `Workload contains ${workload.connectedJobs.length} connected jobs`,
                    `${workload.schedules.length} schedule(s), ${workload.tasks.length} task(s)`,
                    ...positiveDescriptions
                ]
            }
            
            workloadScores.push({
                workload,
                score: workloadScore,
                jobScores: ctx.jobs as Record<string, ScoreResult<WorkloadJobHolder>>
            })
        } catch (e) {
            // Log error but continue scoring other workloads
            const workloadKey = `${workload.versionId.groupName}/${workload.versionId.projectName}/${workload.versionId.versionName} - ${workload.startingJob}`
            console.warn(`Failed to score workload ${workloadKey}:`, e)
        }
    }
    
    return {
        workloadScores,
        totalWorkloads: workloads.length
    }
}